import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RefreshCw, Check, X } from 'lucide-react';

interface SliderCaptchaProps {
  onVerify: (verified: boolean, token: string) => void;
  onError?: (error: string) => void;
}

interface PuzzlePiece {
  x: number;
  y: number;
  path: string;
}

const CANVAS_WIDTH = 280;
const CANVAS_HEIGHT = 160;
const PIECE_SIZE = 44;
const TOLERANCE = 5;

export const SliderCaptcha: React.FC<SliderCaptchaProps> = ({ onVerify, onError }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [status, setStatus] = useState<'idle' | 'success' | 'failed'>('idle');
  const [puzzlePiece, setPuzzlePiece] = useState<PuzzlePiece | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pieceCanvasRef = useRef<HTMLCanvasElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const generatePuzzlePath = useCallback((x: number, y: number, size: number): string => {
    const tabSize = size * 0.2;
    const tabDirection = Math.random() > 0.5 ? 1 : -1;

    return `
      M ${x} ${y}
      L ${x + size * 0.4} ${y}
      C ${x + size * 0.4} ${y - tabSize * tabDirection},
        ${x + size * 0.6} ${y - tabSize * tabDirection},
        ${x + size * 0.6} ${y}
      L ${x + size} ${y}
      L ${x + size} ${y + size * 0.4}
      C ${x + size + tabSize} ${y + size * 0.4},
        ${x + size + tabSize} ${y + size * 0.6},
        ${x + size} ${y + size * 0.6}
      L ${x + size} ${y + size}
      L ${x + size * 0.6} ${y + size}
      C ${x + size * 0.6} ${y + size + tabSize * tabDirection},
        ${x + size * 0.4} ${y + size + tabSize * tabDirection},
        ${x + size * 0.4} ${y + size}
      L ${x} ${y + size}
      L ${x} ${y + size * 0.6}
      C ${x - tabSize} ${y + size * 0.6},
        ${x - tabSize} ${y + size * 0.4},
        ${x} ${y + size * 0.4}
      Z
    `;
  }, []);

  const initializePuzzle = useCallback(() => {
    const canvas = canvasRef.current;
    const pieceCanvas = pieceCanvasRef.current;
    if (!canvas || !pieceCanvas) return;

    const ctx = canvas.getContext('2d');
    const pieceCtx = pieceCanvas.getContext('2d');
    if (!ctx || !pieceCtx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    const imageId = Math.floor(Math.random() * 100) + 1;
    img.src = `https://picsum.photos/seed/${imageId}/${CANVAS_WIDTH}/${CANVAS_HEIGHT}`;

    img.onload = () => {
      imageRef.current = img;

      ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      const pieceX = Math.random() * (CANVAS_WIDTH - PIECE_SIZE * 2 - 60) + PIECE_SIZE + 60;
      const pieceY = Math.random() * (CANVAS_HEIGHT - PIECE_SIZE * 2 - 20) + PIECE_SIZE;

      const path = generatePuzzlePath(pieceX, pieceY, PIECE_SIZE);

      const path2d = new Path2D(path);
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fill(path2d);
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.stroke(path2d);
      ctx.restore();

      pieceCanvas.width = PIECE_SIZE + 20;
      pieceCanvas.height = PIECE_SIZE + 20;

      pieceCtx.save();

      const piecePath = generatePuzzlePath(10, 10, PIECE_SIZE);
      const piecePath2d = new Path2D(piecePath);

      pieceCtx.clip(piecePath2d);
      pieceCtx.drawImage(
        img,
        pieceX - 10,
        pieceY - 10,
        PIECE_SIZE + 20,
        PIECE_SIZE + 20,
        0,
        0,
        PIECE_SIZE + 20,
        PIECE_SIZE + 20
      );
      pieceCtx.restore();

      pieceCtx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      pieceCtx.lineWidth = 2;
      pieceCtx.stroke(piecePath2d);

      pieceCtx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      pieceCtx.shadowBlur = 4;
      pieceCtx.shadowOffsetX = 2;
      pieceCtx.shadowOffsetY = 2;

      setPuzzlePiece({
        x: pieceX,
        y: pieceY,
        path,
      });

      setImageLoaded(true);
    };

    img.onerror = () => {
      onError?.('Failed to load captcha image');
    };
  }, [generatePuzzlePath, onError]);

  useEffect(() => {
    initializePuzzle();
  }, [initializePuzzle]);

  const handleRefresh = () => {
    setStatus('idle');
    setSliderPosition(0);
    setImageLoaded(false);
    setPuzzlePiece(null);
    initializePuzzle();
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (status !== 'idle') return;
    setIsDragging(true);
    e.preventDefault();
  };

  const handleDragMove = useCallback(
    (clientX: number) => {
      if (!isDragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const maxPosition = CANVAS_WIDTH - 44;
      const newPosition = Math.max(0, Math.min(clientX - rect.left - 22, maxPosition));

      setSliderPosition(newPosition);
    },
    [isDragging]
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging || !puzzlePiece) return;
    setIsDragging(false);

    const targetX = puzzlePiece.x - 10;
    const isCorrect = Math.abs(sliderPosition - targetX) <= TOLERANCE;

    if (isCorrect) {
      setStatus('success');
      const token = btoa(
        JSON.stringify({
          timestamp: Date.now(),
          position: sliderPosition,
          verified: true,
        })
      );
      onVerify(true, token);
    } else {
      setStatus('failed');
      setTimeout(() => {
        setStatus('idle');
        setSliderPosition(0);
      }, 1000);
      onVerify(false, '');
    }
  }, [isDragging, puzzlePiece, sliderPosition, onVerify]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientX);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleDragMove(e.touches[0].clientX);
      }
    };
    const handleEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-primary-600';
    }
  };

  const getTrackColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900/30';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30';
      default:
        return 'bg-gray-100 dark:bg-gray-700';
    }
  };

  return (
    <div className="w-full max-w-[300px] mx-auto">
      <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
        <div className="relative" style={{ height: CANVAS_HEIGHT }}>
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="w-full h-full"
          />

          {imageLoaded && puzzlePiece && (
            <canvas
              ref={pieceCanvasRef}
              className="absolute top-0 left-0 pointer-events-none transition-transform duration-75"
              style={{
                transform: `translateX(${sliderPosition}px) translateY(${puzzlePiece.y - 10}px)`,
              }}
            />
          )}

          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}

          <button
            onClick={handleRefresh}
            className="absolute top-2 right-2 p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-700 transition-colors"
            type="button"
          >
            <RefreshCw className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div
          ref={containerRef}
          className={`relative h-12 ${getTrackColor()} transition-colors duration-300`}
        >
          <div
            className={`absolute left-0 top-0 h-full ${getStatusColor()} transition-all duration-300 rounded-r`}
            style={{ width: sliderPosition + 44 }}
          />

          <div
            ref={sliderRef}
            className={`absolute top-1 h-10 w-10 rounded cursor-grab active:cursor-grabbing
              ${getStatusColor()} shadow-lg flex items-center justify-center
              transition-colors duration-300 select-none touch-none`}
            style={{ left: sliderPosition + 2 }}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            {status === 'success' ? (
              <Check className="w-5 h-5 text-white" />
            ) : status === 'failed' ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <div className="flex gap-0.5">
                <div className="w-0.5 h-4 bg-white/80 rounded" />
                <div className="w-0.5 h-4 bg-white/80 rounded" />
                <div className="w-0.5 h-4 bg-white/80 rounded" />
              </div>
            )}
          </div>

          {status === 'idle' && sliderPosition === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-12">
                Slide to verify
              </span>
            </div>
          )}
        </div>
      </div>

      {status === 'success' && (
        <p className="text-center text-sm text-green-600 dark:text-green-400 mt-2">
          Verification successful
        </p>
      )}

      {status === 'failed' && (
        <p className="text-center text-sm text-red-600 dark:text-red-400 mt-2">
          Please try again
        </p>
      )}
    </div>
  );
};
